
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase, type BlogPost } from '../../utils/supabase';
import Breadcrumb from '../../components/feature/Breadcrumb';

const POSTS_PER_PAGE = 6;

const blogCategories = [
  'Todos',
  'Gestão de Frotas',
  'Documentos Fiscais',
  'Financeiro',
  'Estoque',
  'Relatórios',
  'Tecnologia'
];

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  // Schema.org Blog structured data
  useEffect(() => {
    document.title = 'Blog TG WEB | Conteúdo para Gestão de Transportadoras';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Artigos e conteúdos especializados sobre gestão de transportadoras, frotas, documentos fiscais, financeiro e tecnologia. Blog TG WEB.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Artigos e conteúdos especializados sobre gestão de transportadoras, frotas, documentos fiscais, financeiro e tecnologia. Blog TG WEB.';
      document.head.appendChild(meta);
    }

    if (posts.length === 0) return;

    const blogSchema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Blog TG WEB',
      description: 'Conteúdo especializado para gestão de transportadoras',
      url: window.location.href,
      blogPost: posts.map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        author: {
          '@type': 'Person',
          name: post.author,
        },
        datePublished: post.published_at,
        image: post.image_url,
        url: `${window.location.origin}/blog/${post.slug}`,
        articleSection: post.category,
        wordCount: post.reading_time ? post.reading_time * 200 : undefined,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-schema';
    script.textContent = JSON.stringify(blogSchema);

    const existing = document.getElementById('blog-schema');
    if (existing) existing.remove();

    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('blog-schema');
      if (el) el.remove();
    };
  }, [posts]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Erro ao buscar posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar posts
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, selectedCategory]);

  // Paginação
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  // Posts mais populares
  const popularPosts = useMemo(() => {
    return [...posts]
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }, [posts]);

  // Artigo em destaque (primeiro da lista filtrada)
  const featuredPost = filteredPosts[0];

  // Posts para o grid (excluindo o destaque)
  const gridPosts = paginatedPosts.filter(post => post.id !== featuredPost?.id);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') {
          alert('Este e-mail já está cadastrado!');
        } else {
          throw error;
        }
      } else {
        setSubmitSuccess(true);
        setEmail('');
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      alert('Erro ao cadastrar. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-primary mb-4"></i>
          <p className="text-neutral-600">Carregando artigos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero com Busca */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-10 sm:pb-12 lg:pb-16 bg-gradient-to-br from-primary to-primary-800">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Blog' },
            ]}
          />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            Blog TG WEB
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
            Conteúdo especializado para gestão de transportadoras
          </p>
          
          {/* Barra de Busca */}
          <div className="max-w-xl mx-auto relative">
            <div className="w-8 h-8 flex items-center justify-center absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-neutral-400">
              <i className="ri-search-line text-lg sm:text-xl"></i>
            </div>
            <input
              type="text"
              placeholder="Buscar artigos..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 rounded-xl text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-secondary shadow-lg text-sm"
            />
          </div>
        </div>
      </section>

      {/* Artigo em Destaque */}
      {featuredPost && currentPage === 1 && !searchQuery && selectedCategory === 'Todos' && (
        <section className="py-8 sm:py-12 bg-neutral-50">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4 sm:mb-6">Em Destaque</h2>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="group grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-hover transition-all cursor-pointer"
            >
              <div className="relative h-48 sm:h-56 md:h-64 lg:h-80 overflow-hidden">
                <img
                  src={featuredPost.image_url}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                  <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-secondary text-white text-xs sm:text-sm font-semibold rounded-full">
                    {featuredPost.category}
                  </span>
                </div>
              </div>
              <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4 group-hover:text-primary transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-neutral-600 mb-4 sm:mb-6 line-clamp-3 text-sm sm:text-base">
                  {featuredPost.excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-neutral-500">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <i className="ri-user-line"></i>
                    <span>{featuredPost.author}</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <i className="ri-calendar-line"></i>
                    <span>{new Date(featuredPost.published_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <i className="ri-time-line"></i>
                    <span>{featuredPost.reading_time} min</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Filtro por Categorias */}
      <section className="py-4 sm:py-6 lg:py-8 border-b border-neutral-200">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {blogCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid de Artigos + Sidebar */}
      <section className="py-8 sm:py-12">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Área Principal - 2/3 */}
            <div className="lg:col-span-2">
              {gridPosts.length > 0 ? (
                <>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    {gridPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="group bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-hover transition-all cursor-pointer border border-neutral-100"
                      >
                        <div className="relative h-36 sm:h-40 lg:h-44 overflow-hidden">
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
                            <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/90 backdrop-blur-sm text-primary text-[10px] sm:text-xs font-semibold rounded-full">
                              {post.category}
                            </span>
                          </div>
                        </div>
                        <div className="p-4 sm:p-5">
                          <h3 className="text-sm sm:text-base font-bold text-neutral-900 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-neutral-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-[10px] sm:text-xs text-neutral-500">
                            <span className="truncate max-w-[100px]">{post.author}</span>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="flex items-center gap-1">
                                <i className="ri-eye-line"></i>
                                {post.views}
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {post.reading_time} min
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Paginação */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap text-sm ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 sm:py-16">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 bg-neutral-100 rounded-full">
                    <i className="ri-article-line text-2xl sm:text-3xl text-neutral-400"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-2">
                    Nenhum artigo encontrado
                  </h3>
                  <p className="text-neutral-600 text-sm sm:text-base">
                    Tente ajustar sua busca ou filtros
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar - 1/3 */}
            <div className="lg:col-span-1 space-y-6 sm:space-y-8">
              {/* Mais Populares */}
              {popularPosts.length > 0 && (
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-soft border border-neutral-100">
                  <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-3 sm:mb-4 flex items-center gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-secondary">
                      <i className="ri-fire-line"></i>
                    </div>
                    Mais Populares
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                    {popularPosts.map((post, index) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="flex gap-2 sm:gap-3 group cursor-pointer"
                      >
                        <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-primary/10 text-primary font-bold rounded-lg text-xs sm:text-sm">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs sm:text-sm font-medium text-neutral-900 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h4>
                          <span className="text-[10px] sm:text-xs text-neutral-500 flex items-center gap-1 mt-1">
                            <i className="ri-eye-line"></i>
                            {post.views.toLocaleString()} views
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA WhatsApp */}
              <div className="bg-gradient-to-br from-primary to-primary-800 rounded-xl p-5 sm:p-6 text-white">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl mb-3 sm:mb-4">
                  <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold mb-2">Precisa de Ajuda?</h3>
                <p className="text-white/90 text-xs sm:text-sm mb-3 sm:mb-4">
                  Fale com nossos especialistas e tire suas dúvidas sobre gestão de transportadoras.
                </p>
                <a
                  href="https://wa.me/5511999999999?text=Olá,%20vim%20do%20blog%20e%20gostaria%20de%20mais%20informações"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white text-primary font-semibold rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer whitespace-nowrap text-xs sm:text-sm"
                >
                  Falar no WhatsApp
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4 sm:mb-6">
            <i className="ri-mail-send-line text-2xl sm:text-3xl text-white"></i>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
            Receba Conteúdo Exclusivo
          </h2>
          <p className="text-white/90 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 max-w-xl mx-auto px-2">
            Inscreva-se na nossa newsletter e receba dicas, novidades e conteúdos exclusivos sobre gestão de transportadoras.
          </p>
          
          <form
            id="newsletter-form"
            data-readdy-form
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-lg mx-auto"
          >
            <input
              type="email"
              name="email"
              placeholder="Seu melhor e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 px-4 sm:px-5 py-3 sm:py-4 rounded-xl text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-secondary text-white font-semibold rounded-xl hover:bg-secondary-600 transition-all shadow-lg disabled:opacity-70 cursor-pointer whitespace-nowrap text-sm sm:text-base"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando...
                </span>
              ) : (
                'Inscrever-se'
              )}
            </button>
          </form>

          {submitSuccess && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg text-white text-sm">
              <i className="ri-check-line"></i>
              Inscrição realizada com sucesso!
            </div>
          )}
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-12 sm:py-16 bg-neutral-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-6 sm:mb-8 text-center">
            Conheça Nosso Sistema
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            <Link
              to="/funcionalidades"
              className="group p-4 sm:p-5 bg-white rounded-xl hover:shadow-hover transition-all border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-apps-line text-lg sm:text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Funcionalidades
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Emissão de CT-e e mais
              </p>
            </Link>

            <Link
              to="/planos"
              className="group p-4 sm:p-5 bg-white rounded-xl hover:shadow-hover transition-all border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-price-tag-3-line text-lg sm:text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Planos
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Escolha o plano ideal
              </p>
            </Link>

            <Link
              to="/sistema-emissao-cte"
              className="group p-4 sm:p-5 bg-white rounded-xl hover:shadow-hover transition-all border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-file-text-line text-lg sm:text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Emissor CT-e
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Guia de emissão
              </p>
            </Link>

            <Link
              to="/faq"
              className="group p-4 sm:p-5 bg-white rounded-xl hover:shadow-hover transition-all border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-question-answer-line text-lg sm:text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                FAQ
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Tire suas dúvidas
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
